import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { Label } from '../ui/Label';

type Props = {
  label: ReactNode;
  /** `htmlFor` on the label; pair with `id` on the wrapped input for a11y. */
  htmlFor?: string;
  /** Validation message rendered below the input. Falsy hides the slot. */
  error?: ReactNode;
  /** Optional one-liner shown beneath the input (above any error). */
  description?: ReactNode;
  /** Mark the field as required visually (no native HTML required attribute). */
  required?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Standard label + input + optional description + optional error stack. Pair
 * with `<Form>` + `useForm`:
 *
 *   <FormField label="Name" htmlFor="name" error={form.errors.name}>
 *     <Input id="name" value={form.values.name}
 *            onChange={(e) => form.set('name', e.target.value)} />
 *   </FormField>
 */
export function FormField({
  label,
  htmlFor,
  error,
  description,
  required,
  className,
  children,
}: Props) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {description && !error && <p className="text-xs text-muted-foreground">{description}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
